import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useListUiState } from '../hooks/useListUiState'
import { useScrollRestoration, clearScrollRestoration } from '../hooks/useScrollRestoration'
import { buildReturnToFromLocation } from '../utils/navigationReturnTo'
import { searchPublicExercises, getVideoRanksForExercises } from '../services/exerciseService'
import { formatTagForDisplay } from '../utils/tagFormatter'
import { CLASSICAL_FORMATIONS, CLASSICAL_GENRES, inferFormationsFromWorkTitle, inferGenreFromWorkTitle } from '../data/formations'
import { FormationInstrumentPicker } from '../components/FormationInstrumentPicker'
import { categorizeTags as categorizeTagsUtil } from '../utils/tagCategories'
import { getDisplayDifficulty } from '../utils/difficultyFromContent'
import { getComposerDisplayName, getComposerDisplayNameShort, getComposerFamilyName } from '../utils/exerciseDisplay'
import ExerciseCard from '../components/ExerciseCard'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import './FreeMode.css'

const COMPOSERS_VISIBLE_INITIAL = 12
const TAGS_PER_SECTION_VISIBLE = 15

const FREE_MODE_LIST_FIELDS = {
  q: { type: 'string', default: '', debounce: true },
  composer: { type: 'string', default: '' },
  difficulty: { type: 'string', default: '' },
  tags: { type: 'string[]', default: [] },
  genre: { type: 'string', default: '' },
  formations: { type: 'string[]', default: [] },
  done: { type: 'string', default: 'all' },
  horizons: { type: 'string', default: 'classic' },
  sort: { type: 'string', default: 'date' },
}

function FreeMode({
  doneExerciseIds = [],
  initialFilter = null,
  onInitialFilterConsumed,
  horizonsUnlocked = false,
  onOpenCodex = null,
  publicTeaserOnly = false,
}) {
  const navigate = useNavigate()
  const [inputMode, setInputMode] = useState(() => {
    if (typeof window === 'undefined') return 'qcm'
    const saved = window.localStorage.getItem('opus:free-input-mode')
    return saved === 'full' ? 'full' : 'qcm'
  }) // 'qcm' | 'full'
  
  const location = useLocation()

  const {
    values: listValues,
    inputValues: listInput,
    setField: setListField,
    setFields: setListFields,
    setDraft: setListDraft,
    reset: resetListFilters,
    hasActive: listHasActive,
  } = useListUiState({
    fields: FREE_MODE_LIST_FIELDS,
    preserveKeys: ['tab', 'fiche', 'node', 'section', 'hw'],
    debounceMs: 300,
  })



  const horizonsFilter = listValues.horizons
  const setHorizonsFilter = useCallback((v) => setListField('horizons', v), [setListField])
  const searchText = listInput.q
  const setSearchText = useCallback((v) => {
    setListDraft('q', typeof v === 'function' ? v(listInput.q) : v)
  }, [setListDraft, listInput.q])
  const debouncedSearchText = listValues.q
  const selectedComposer = listValues.composer
  const setSelectedComposer = useCallback((v) => setListField('composer', v), [setListField])
  const selectedDifficulty = listValues.difficulty
  const setSelectedDifficulty = useCallback((v) => setListField('difficulty', v), [setListField])
  const selectedTags = listValues.tags
  const setSelectedTags = useCallback((v) => setListField('tags', v), [setListField])
  const selectedGenre = listValues.genre
  const setSelectedGenre = useCallback((v) => setListField('genre', v), [setListField])
  const selectedFormations = listValues.formations
  const setSelectedFormations = useCallback((v) => setListField('formations', v), [setListField])
  const selectedDoneStatus = listValues.done
  const setSelectedDoneStatus = useCallback((v) => setListField('done', v), [setListField])
  const sortBy = listValues.sort
  const setSortBy = useCallback((v) => setListField('sort', v), [setListField])
  
  // États des données (allExercises = tous les exercices publics y compris Horizons)
  const [allExercises, setAllExercises] = useState([])
  const [filteredExercises, setFilteredExercises] = useState([])
  const [loading, setLoading] = useState(true)
  const [videoRanksMap, setVideoRanksMap] = useState(() => new Map())
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const dismissFilterDrawer = useCallback(() => setIsFilterDrawerOpen(false), [])
  const filterDrawerBackdropDismiss = useBackdropDismiss(dismissFilterDrawer)
  const [composersExpanded, setComposersExpanded] = useState(false)
  const [expandedTagSections, setExpandedTagSections] = useState(() => new Set()) // clés de catégorie (cadences, renversements, etc.)

  // Exercices visibles selon le filtre Horizons
  const exercises = useMemo(() => {
    if (horizonsFilter === 'horizons') return allExercises.filter(ex => ex.metadata?.section === 'horizons')
    if (horizonsFilter === 'all') return allExercises
    return allExercises.filter(ex => ex.metadata?.section !== 'horizons')
  }, [allExercises, horizonsFilter])

  useEffect(() => {
    if (!exercises.length) {
      setVideoRanksMap(new Map())
      return
    }
    getVideoRanksForExercises(exercises).then(setVideoRanksMap).catch(() => setVideoRanksMap(new Map()))
  }, [exercises])

  // Compositeurs dérivés des exercices visibles (format court "Nom X.", triés par nom de famille)
  const composers = useMemo(() => {
    const seen = new Set()
    const withFamily = []
    exercises.forEach(ex => {
      const display = getComposerDisplayNameShort(ex.metadata)
      if (!display || seen.has(display)) return
      seen.add(display)
      withFamily.push({ display, familyName: getComposerFamilyName(ex.metadata) })
    })
    return withFamily.sort((a, b) => a.familyName.localeCompare(b.familyName, undefined, { sensitivity: 'base' })).map(x => x.display)
  }, [exercises])

  // Tags dérivés des exercices visibles uniquement (aucun tag sans exercice associé)
  const availableTags = useMemo(() => {
    const set = new Set()
    exercises.forEach(ex => {
      if (ex.autoTags && Array.isArray(ex.autoTags)) {
        ex.autoTags.forEach(tag => set.add(tag))
      }
    })
    return [...set].sort()
  }, [exercises])

  // Catégories de tags dérivées
  const tagCategories = useMemo(() => categorizeTagsUtil(availableTags), [availableTags])

  // Formations et genres (type d'œuvre) — classique uniquement
  /** Ids de formation déjà présents dans le catalogue (priorité dans l’autocomplete) */
  const formationCorpusIds = useMemo(() => {
    const ids = new Set()
    exercises.forEach((ex) => {
      if (ex.metadata?.section === 'horizons') return
      const raw = ex.metadata?.formation
      const arr = Array.isArray(raw) ? raw : raw ? [raw] : inferFormationsFromWorkTitle(ex.metadata?.workTitle || '', null)
      arr.forEach((id) => ids.add(id))
    })
    return [...ids]
  }, [exercises])

  const genreOptions = useMemo(() => {
    const ids = new Set()
    exercises.forEach(ex => {
      if (ex.metadata?.section === 'horizons') return
      const id = ex.metadata?.genre || inferGenreFromWorkTitle(ex.metadata?.workTitle || '')
      if (id) ids.add(id)
    })
    return CLASSICAL_GENRES.filter(g => ids.has(g.id))
  }, [exercises])

  
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('opus:free-input-mode', inputMode)
  }, [inputMode])

  // doneSet et filterExercises doivent être déclarés AVANT le useEffect qui les utilise (évite ReferenceError "Cannot access before initialization")
  const doneSet = useMemo(() => new Set(doneExerciseIds || []), [doneExerciseIds])

  const filterExercises = useCallback(() => {
    let filtered = [...exercises]
    if (selectedDoneStatus === 'done') {
      filtered = filtered.filter(ex => doneSet.has(ex.id))
    } else if (selectedDoneStatus === 'not-done') {
      filtered = filtered.filter(ex => !doneSet.has(ex.id))
    }
    if (debouncedSearchText.trim()) {
      const searchLower = debouncedSearchText.toLowerCase()
      filtered = filtered.filter(ex => {
        const workTitle = ex.metadata?.workTitle || ''
        const exerciseTitle = ex.metadata?.exerciseTitle || ''
        const composer = getComposerDisplayName(ex.metadata) || ''
        return (
          workTitle.toLowerCase().includes(searchLower) ||
          exerciseTitle.toLowerCase().includes(searchLower) ||
          composer.toLowerCase().includes(searchLower)
        )
      })
    }
    if (selectedComposer) {
      filtered = filtered.filter(ex => getComposerDisplayNameShort(ex.metadata) === selectedComposer)
    }
    if (selectedDifficulty) {
      filtered = filtered.filter(ex => getDisplayDifficulty(ex.metadata?.difficulty) === selectedDifficulty)
    }
    if (selectedTags.length > 0) {
      filtered = filtered.filter(ex => {
        const exerciseTags = ex.autoTags || []
        return selectedTags.some(tag => exerciseTags.includes(tag))
      })
    }
    if (selectedGenre) {
      filtered = filtered.filter(ex => {
        const genreId = ex.metadata?.genre || inferGenreFromWorkTitle(ex.metadata?.workTitle || '')
        return genreId === selectedGenre
      })
    }
    if (selectedFormations.length > 0) {
      filtered = filtered.filter(ex => {
        const formationRaw = ex.metadata?.formation
        const exFormations = Array.isArray(formationRaw) ? formationRaw : (formationRaw ? [formationRaw] : inferFormationsFromWorkTitle(ex.metadata?.workTitle || '', null))
        return selectedFormations.every(s => exFormations.includes(s))
      })
    }
    setFilteredExercises(filtered)
  }, [debouncedSearchText, selectedComposer, selectedDifficulty, selectedTags, selectedGenre, selectedFormations, selectedDoneStatus, exercises, doneSet])

  // Filtrer les exercices quand les filtres changent
  useEffect(() => {
    filterExercises()
  }, [debouncedSearchText, selectedComposer, selectedDifficulty, selectedTags, selectedGenre, selectedFormations, selectedDoneStatus, exercises, doneSet, filterExercises])

  // Appliquer le filtre initial (clic pastille depuis le dashboard élève)
  useEffect(() => {
    if (!initialFilter || !onInitialFilterConsumed) return
    const urlAlreadyFiltered = Boolean(
      listValues.q || listValues.composer || listValues.difficulty || (listValues.tags && listValues.tags.length) ||
      listValues.genre || (listValues.formations && listValues.formations.length) || listValues.done !== 'all'
    )
    if (!urlAlreadyFiltered) {
      const patch = {}
      if (initialFilter.difficulty) patch.difficulty = initialFilter.difficulty
      if (initialFilter.tag) patch.tags = [initialFilter.tag]
      if (initialFilter.doneStatus === 'done' || initialFilter.doneStatus === 'not-done') patch.done = initialFilter.doneStatus
      if (initialFilter.genre) patch.genre = initialFilter.genre
      if (initialFilter.formation) patch.formations = [initialFilter.formation]
      if (Object.keys(patch).length) setListFields(patch)
    }
    onInitialFilterConsumed()
  }, [initialFilter, onInitialFilterConsumed, listValues, setListFields])

  // Tri des résultats (stable : createdAt puis id en départage ; champs vides en fin de liste pour titre/compositeur)
  const sortedExercises = useMemo(() => {
    const list = [...filteredExercises]
    const getDate = (ex) => ex.createdAt?.toDate?.() || new Date(0)
    const getWorkTitle = (ex) => (ex.metadata?.workTitle || '').trim()
    const getExerciseTitle = (ex) => (ex.metadata?.exerciseTitle || '').trim()
    const getComposerFamily = (ex) => getComposerFamilyName(ex.metadata).trim()
    list.sort((a, b) => {
      if (sortBy === 'date') {
        const d = getDate(b) - getDate(a)
        if (d !== 0) return d
        return (a.id || '').localeCompare(b.id || '')
      }
      if (sortBy === 'title') {
        const wa = getWorkTitle(a)
        const wb = getWorkTitle(b)
        const emptyA = !wa
        const emptyB = !wb
        if (emptyA && emptyB) {
          const ea = getExerciseTitle(a).toLowerCase()
          const eb = getExerciseTitle(b).toLowerCase()
          const c = ea.localeCompare(eb)
          if (c !== 0) return c
          return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
        } else if (emptyA) return 1
        else if (emptyB) return -1
        const c = wa.localeCompare(wb, undefined, { sensitivity: 'base' })
        if (c !== 0) return c
        const ea = getExerciseTitle(a)
        const eb = getExerciseTitle(b)
        const c2 = ea.localeCompare(eb, undefined, { sensitivity: 'base' })
        if (c2 !== 0) return c2
        return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
      }
      // sortBy === 'composer' (tri par nom de famille)
      const ca = getComposerFamily(a)
      const cb = getComposerFamily(b)
      const emptyA = !ca
      const emptyB = !cb
      if (emptyA && emptyB) return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
      if (emptyA) return 1
      if (emptyB) return -1
      const c = ca.localeCompare(cb, undefined, { sensitivity: 'base' })
      if (c !== 0) return c
      const c2 = getWorkTitle(a).localeCompare(getWorkTitle(b), undefined, { sensitivity: 'base' })
      if (c2 !== 0) return c2
      return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
    })
    return list
  }, [filteredExercises, sortBy])
  
  const loadAllExercises = useCallback(async () => {
    try {
      setLoading(true)
      const data = await searchPublicExercises(
        publicTeaserOnly ? { publicTeaserOnly: true } : {}
      )
      setAllExercises(data)
    } catch (error) {
      console.error('Erreur lors du chargement des exercices:', error)
    } finally {
      setLoading(false)
    }
  }, [publicTeaserOnly])

  useEffect(() => {
    loadAllExercises()
  }, [loadAllExercises])

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }
  
  const clearAllFilters = () => {
    resetListFilters()
    clearScrollRestoration('free-mode')
    // keep tab=free-mode
    setListFields({})
  }

  const freeModeReturnTo = useMemo(() => buildReturnToFromLocation(location), [location])

  useScrollRestoration('free-mode', { ready: !loading })

  const handleExerciseClick = (exerciseId) => {
    navigate(`/play/${exerciseId}?mode=libre&inputMode=${inputMode}`, {
      state: { returnTo: freeModeReturnTo },
    })
  }
  
  const difficulties = ['facile', 'intermédiaire', 'difficile', 'expert']

  // Pastilles "notion" pour accès rapide (alignées sur les cartes exercice)
  const notionQuickFilters = useMemo(() => {
    const items = []
    if (tagCategories.cadences?.[0]) items.push({ label: 'Cadences', tag: tagCategories.cadences[0] })
    if (tagCategories.renversements?.[0]) items.push({ label: 'Renversements', tag: tagCategories.renversements[0] })
    const septieme = tagCategories.extensions?.find((t) => /septième|septieme/i.test(t))
    if (septieme) items.push({ label: '7e', tag: septieme })
    if (tagCategories.qualites?.[0]) items.push({ label: 'Couleurs', tag: tagCategories.qualites[0] })
    if (tagCategories.structure?.[0]) items.push({ label: 'Structure', tag: tagCategories.structure[0] })
    ;['Baroque', 'Classique', 'Romantique', 'Moderne'].forEach((s) => {
      const t = tagCategories.styles?.find((st) => st.toLowerCase() === s.toLowerCase())
      if (t) items.push({ label: s, tag: t })
    })
    return items
  }, [tagCategories])

  const hasActiveFilters = debouncedSearchText || selectedComposer || selectedDifficulty || selectedTags.length > 0 || selectedGenre || selectedFormations.length > 0 || selectedDoneStatus !== 'all' || horizonsFilter !== 'classic'

  const applyNotionQuickFilter = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const clearAdvancedFilters = () => {
    setSelectedComposer('')
    setSelectedTags([])
    setSelectedGenre('')
    setSelectedFormations([])
  }

  const removeLastAppliedFilter = () => {
    if (selectedTags.length > 0) {
      setSelectedTags((prev) => prev.slice(0, -1))
      return
    }
    if (selectedFormations.length > 0) {
      setSelectedFormations((prev) => prev.slice(0, -1))
      return
    }
    if (selectedGenre) {
      setSelectedGenre('')
      return
    }
    if (selectedComposer) {
      setSelectedComposer('')
      return
    }
    if (selectedDifficulty) {
      setSelectedDifficulty('')
      return
    }
    if (selectedDoneStatus !== 'all') {
      setSelectedDoneStatus('all')
      return
    }
    if (debouncedSearchText.trim()) {
      setSearchText('')
      return
    }
    if (horizonsFilter !== 'classic') {
      setHorizonsFilter('classic')
    }
  }

  const activeFilterCount = useMemo(() => {
    return [
      debouncedSearchText.trim() ? 1 : 0,
      selectedComposer ? 1 : 0,
      selectedDifficulty ? 1 : 0,
      selectedTags.length,
      selectedGenre ? 1 : 0,
      selectedFormations.length,
      selectedDoneStatus !== 'all' ? 1 : 0,
      horizonsFilter !== 'classic' ? 1 : 0
    ].reduce((sum, n) => sum + n, 0)
  }, [
    debouncedSearchText,
    selectedComposer,
    selectedDifficulty,
    selectedTags.length,
    selectedGenre,
    selectedFormations.length,
    selectedDoneStatus,
    horizonsFilter
  ])

  const statusCounts = useMemo(() => {
    let allCount = 0
    let doneCount = 0
    let notDoneCount = 0
    exercises.forEach((ex) => {
      allCount += 1
      if (doneSet.has(ex.id)) doneCount += 1
      else notDoneCount += 1
    })
    return { all: allCount, done: doneCount, 'not-done': notDoneCount }
  }, [exercises, doneSet])

  const difficultyCounts = useMemo(() => {
    const counts = { facile: 0, intermédiaire: 0, difficile: 0, expert: 0 }
    exercises.forEach((ex) => {
      const diff = getDisplayDifficulty(ex.metadata?.difficulty)
      if (counts[diff] !== undefined) counts[diff] += 1
    })
    return counts
  }, [exercises])

  const activeFilterChips = useMemo(() => {
    const chips = []
    if (debouncedSearchText.trim()) {
      chips.push({ id: `search:${debouncedSearchText}`, label: `Recherche : ${debouncedSearchText}`, type: 'search', value: debouncedSearchText })
    }
    if (horizonsFilter !== 'classic') {
      chips.push({
        id: `horizons:${horizonsFilter}`,
        label: horizonsFilter === 'all' ? 'Section : Tous' : 'Section : Horizons',
        type: 'horizons',
        value: horizonsFilter
      })
    }
    if (selectedDoneStatus !== 'all') {
      chips.push({
        id: `status:${selectedDoneStatus}`,
        label: selectedDoneStatus === 'done' ? 'Statut : Déjà fait' : 'Statut : Pas encore fait',
        type: 'status',
        value: selectedDoneStatus
      })
    }
    if (selectedDifficulty) {
      chips.push({ id: `difficulty:${selectedDifficulty}`, label: `Niveau : ${selectedDifficulty}`, type: 'difficulty', value: selectedDifficulty })
    }
    if (selectedComposer) {
      chips.push({ id: `composer:${selectedComposer}`, label: `Compositeur : ${selectedComposer}`, type: 'composer', value: selectedComposer })
    }
    if (selectedGenre) {
      const genreLabel = genreOptions.find((g) => g.id === selectedGenre)?.label || selectedGenre
      chips.push({ id: `genre:${selectedGenre}`, label: `Genre : ${genreLabel}`, type: 'genre', value: selectedGenre })
    }
    selectedFormations.forEach((formationId) => {
      const formationLabel = CLASSICAL_FORMATIONS.find((f) => f.id === formationId)?.label || formationId
      chips.push({ id: `formation:${formationId}`, label: `Formation : ${formationLabel}`, type: 'formation', value: formationId })
    })
    selectedTags.forEach((tag) => {
      const formatted = formatTagForDisplay(tag)
      let prefix = 'Tag'
      if (String(tag).startsWith('Degré') || /^degre/i.test(tag)) prefix = 'Degré'
      else if (/cadence|demi/i.test(tag)) prefix = 'Cadence'
      else if (['Tonique', 'Dominante', 'SousDominante'].includes(tag)) prefix = 'Fonction'
      chips.push({ id: `tag:${tag}`, label: `${prefix} : ${formatted}`, type: 'tag', value: tag })
    })
    return chips
  }, [
    debouncedSearchText,
    horizonsFilter,
    selectedDoneStatus,
    selectedDifficulty,
    selectedComposer,
    selectedGenre,
    selectedFormations,
    selectedTags,
    genreOptions
  ])

  const removeFilterChip = (chip) => {
    if (chip.type === 'search') setSearchText('')
    else if (chip.type === 'horizons') setHorizonsFilter('classic')
    else if (chip.type === 'status') setSelectedDoneStatus('all')
    else if (chip.type === 'difficulty') setSelectedDifficulty('')
    else if (chip.type === 'composer') setSelectedComposer('')
    else if (chip.type === 'genre') setSelectedGenre('')
    else if (chip.type === 'formation') setSelectedFormations((prev) => prev.filter((id) => id !== chip.value))
    else if (chip.type === 'tag') setSelectedTags((prev) => prev.filter((tag) => tag !== chip.value))
  }
  
  return (
    <div className="free-mode-container">
      {/* Barre de recherche */}
      <div className="free-mode-search-bar">
        <div className="free-mode-search-input-wrapper">
          <svg className="free-mode-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Rechercher un exercice (nom de l'œuvre, compositeur...)"
            className="free-mode-search-input"
          />
          {searchText && (
            <button
              className="free-mode-search-clear"
              onClick={() => setSearchText('')}
              aria-label="Effacer"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
        {/* Bouton Filtrer (mobile uniquement) */}
        <button
          className="free-mode-filter-btn"
          onClick={() => setIsFilterDrawerOpen(true)}
          aria-label="Filtrer"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          <span>Filtrer</span>
          {(selectedComposer || selectedDifficulty || selectedTags.length > 0 || selectedGenre || selectedFormations.length > 0 || selectedDoneStatus !== 'all') && (
            <span className="free-mode-filter-badge">{[selectedComposer, selectedDifficulty, selectedTags.length, selectedGenre, selectedFormations.length, selectedDoneStatus !== 'all' ? 1 : 0].filter(Boolean).length}</span>
          )}
        </button>
        {/* Groupe segmenté Nouveaux Horizons (visible uniquement si débloqué) */}
        {horizonsUnlocked && (
          <div className="free-mode-horizons-segmented">
            <button
              type="button"
              className={`free-mode-horizons-seg ${horizonsFilter === 'classic' ? 'active' : ''}`}
              onClick={() => setHorizonsFilter('classic')}
              title="Exercices classiques uniquement"
            >
              Classique
            </button>
            <button
              type="button"
              className={`free-mode-horizons-seg ${horizonsFilter === 'all' ? 'active' : ''}`}
              onClick={() => setHorizonsFilter('all')}
              title="Tous les exercices (classiques + Nouveaux Horizons)"
            >
              Tous
            </button>
            <button
              type="button"
              className={`free-mode-horizons-seg horizons ${horizonsFilter === 'horizons' ? 'active' : ''}`}
              onClick={() => setHorizonsFilter('horizons')}
              title="Exercices Nouveaux Horizons uniquement"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              Horizons
            </button>
          </div>
        )}
        <div className="free-mode-input-mode-control">
          <div className="free-mode-input-mode-label-row">
            <span className="free-mode-input-mode-label">Mode de saisie</span>
          </div>
          <div className="free-mode-input-mode-segmented" role="group" aria-label="Mode de saisie">
            <button
              type="button"
              className={`free-mode-input-mode-seg ${inputMode === 'qcm' ? 'active' : ''}`}
              onClick={() => setInputMode('qcm')}
              title="Guidé : QCM (questions à choix)"
            >
              Guidé
            </button>
            <button
              type="button"
              className={`free-mode-input-mode-seg ${inputMode === 'full' ? 'active' : ''}`}
              onClick={() => setInputMode('full')}
              title="Complet : sélecteur complet"
            >
              Complet
            </button>
          </div>
        </div>
      </div>
      
      <div className="free-mode-content">
        {/* Panneau de filtres (desktop) */}
        <aside className="free-mode-filters">
          <div className="free-mode-filters-header">
            <h3>Filtres</h3>
            {hasActiveFilters && (
              <button className="free-mode-clear-filters" onClick={clearAllFilters}>
                Réinitialiser
              </button>
            )}
          </div>

          <div className="free-mode-filters-scroll-content">

          {/* Bloc Statut : déjà fait / pas encore fait */}
          <details className="free-mode-accordion free-mode-filter-block free-mode-filter-block-statut" open>
            <summary className="free-mode-accordion-summary">
              <h4 className="free-mode-filter-block-title">Statut</h4>
              <span className="free-mode-accordion-chevron" aria-hidden="true"></span>
            </summary>
            <div className="free-mode-accordion-content">
              <div className="free-mode-filter-group">
                <div className="free-mode-statut-chips">
                  <button
                    type="button"
                    className={`free-mode-statut-chip ${selectedDoneStatus === 'all' ? 'free-mode-statut-chip-active' : ''}`}
                    onClick={() => setSelectedDoneStatus('all')}
                  >
                    Tous ({statusCounts.all})
                  </button>
                  <button
                    type="button"
                    className={`free-mode-statut-chip ${selectedDoneStatus === 'done' ? 'free-mode-statut-chip-active' : ''}`}
                    onClick={() => setSelectedDoneStatus('done')}
                  >
                    Déjà fait ({statusCounts.done})
                  </button>
                  <button
                    type="button"
                    className={`free-mode-statut-chip ${selectedDoneStatus === 'not-done' ? 'free-mode-statut-chip-active' : ''}`}
                    onClick={() => setSelectedDoneStatus('not-done')}
                  >
                    Pas encore fait ({statusCounts['not-done']})
                  </button>
                </div>
              </div>
            </div>
          </details>

          {/* Bloc Accès rapide : niveau + pastilles (notions) */}
          <details className="free-mode-accordion free-mode-filter-block free-mode-filter-block-quick" open>
            <summary className="free-mode-accordion-summary">
              <h4 className="free-mode-filter-block-title">Accès rapide</h4>
              <span className="free-mode-accordion-chevron" aria-hidden="true"></span>
            </summary>
            <div className="free-mode-accordion-content">
              <div className="free-mode-filter-group">
                <label className="free-mode-filter-label">Niveau</label>
                <div className="free-mode-difficulty-cards">
                  {difficulties.map(diff => (
                    <button
                      key={diff}
                      className={`free-mode-difficulty-card ${selectedDifficulty === diff ? 'free-mode-difficulty-card-active' : ''}`}
                      onClick={() => setSelectedDifficulty(selectedDifficulty === diff ? '' : diff)}
                    >
                      <span className="free-mode-difficulty-icon">
                        {diff === 'facile' && '🌱'}
                        {diff === 'intermédiaire' && '⭐'}
                        {diff === 'difficile' && '🔥'}
                        {diff === 'expert' && '💎'}
                      </span>
                      <span className="free-mode-difficulty-label">{diff} ({difficultyCounts[diff] || 0})</span>
                    </button>
                  ))}
                </div>
              </div>
              {notionQuickFilters.length > 0 && (
                <div className="free-mode-filter-group">
                  <label className="free-mode-filter-label">Filtrer par notion</label>
                  <div className="free-mode-notion-chips">
                    {notionQuickFilters.map(({ label, tag }) => (
                      <button
                        key={tag}
                        type="button"
                        className={`free-mode-notion-chip ${selectedTags.includes(tag) ? 'free-mode-notion-chip-active' : ''}`}
                        onClick={() => applyNotionQuickFilter(tag)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>

          {/* Bloc Affiner : compositeur, genre */}
          <details className="free-mode-accordion free-mode-filter-block">
            <summary className="free-mode-accordion-summary">
              <h4 className="free-mode-filter-block-title">Affiner</h4>
              <span className="free-mode-accordion-chevron" aria-hidden="true"></span>
            </summary>
            <div className="free-mode-accordion-content">
              <div className="free-mode-filter-group">
                <label className="free-mode-filter-label">Compositeur</label>
                <div className="free-mode-chips-container">
                  <button
                    className={`free-mode-chip ${selectedComposer === '' ? 'free-mode-chip-active' : ''}`}
                    onClick={() => setSelectedComposer('')}
                  >
                    Tous
                  </button>
                  {(composersExpanded ? composers : composers.slice(0, COMPOSERS_VISIBLE_INITIAL)).map(composer => (
                    <button
                      key={composer}
                      className={`free-mode-chip ${selectedComposer === composer ? 'free-mode-chip-active' : ''}`}
                      onClick={() => setSelectedComposer(composer)}
                    >
                      {composer}
                    </button>
                  ))}
                  {composers.length > COMPOSERS_VISIBLE_INITIAL && !composersExpanded && (
                    <button
                      type="button"
                      className="free-mode-chip free-mode-chip-load-more"
                      onClick={() => setComposersExpanded(true)}
                    >
                      Charger plus ({composers.length - COMPOSERS_VISIBLE_INITIAL})
                    </button>
                  )}
                </div>
              </div>
              {genreOptions.length > 0 && (
                <div className="free-mode-filter-group">
                  <label className="free-mode-filter-label">Genre (type d&apos;œuvre)</label>
                  <div className="free-mode-chips-container">
                    <button
                      className={`free-mode-chip ${selectedGenre === '' ? 'free-mode-chip-active' : ''}`}
                      onClick={() => setSelectedGenre('')}
                    >
                      Tous
                    </button>
                    {genreOptions.map(g => (
                      <button
                        key={g.id}
                        className={`free-mode-chip ${selectedGenre === g.id ? 'free-mode-chip-active' : ''}`}
                        onClick={() => setSelectedGenre(selectedGenre === g.id ? '' : g.id)}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="free-mode-filter-group">
                <label className="free-mode-filter-label">Formation (instrumentation)</label>
                <div className="free-mode-chips-container">
                  <button
                    type="button"
                    className={`free-mode-chip ${selectedFormations.length === 0 ? 'free-mode-chip-active' : ''}`}
                    onClick={() => setSelectedFormations([])}
                  >
                    Toutes
                  </button>
                </div>
                <FormationInstrumentPicker
                  masterList={CLASSICAL_FORMATIONS}
                  value={selectedFormations}
                  onChange={setSelectedFormations}
                  prioritizedIds={formationCorpusIds}
                  theme="freeMode"
                  showClearAll={false}
                  hint="Les instruments déjà présents dans le catalogue apparaissent en premier — tape pour rechercher dans la liste complète."
                />
              </div>
            </div>
          </details>

          {/* Bloc Détails techniques : tags par catégorie */}
          <details className="free-mode-accordion free-mode-filter-block free-mode-filter-block-details">
            <summary className="free-mode-accordion-summary">
              <h4 className="free-mode-filter-block-title">Détails techniques</h4>
              <span className="free-mode-accordion-chevron" aria-hidden="true"></span>
            </summary>
            <div className="free-mode-accordion-content">
          
          {/* Section Harmonie - Structure des accords */}
          {(tagCategories.renversements?.length > 0 || tagCategories.extensions?.length > 0 || tagCategories.qualites?.length > 0) && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Structure des accords</h4>
              
              {/* Renversements */}
              {tagCategories.renversements?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Renversements</label>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('renversements') ? tagCategories.renversements : tagCategories.renversements.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.renversements.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('renversements') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'renversements']))}>
                        Charger plus ({tagCategories.renversements.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Extensions */}
              {tagCategories.extensions?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Extensions</label>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('extensions') ? tagCategories.extensions : tagCategories.extensions.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.extensions.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('extensions') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'extensions']))}>
                        Charger plus ({tagCategories.extensions.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Qualités */}
              {tagCategories.qualites?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Qualités</label>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('qualites') ? tagCategories.qualites : tagCategories.qualites.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.qualites.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('qualites') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'qualites']))}>
                        Charger plus ({tagCategories.qualites.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section Harmonie - Accords spéciaux */}
          {tagCategories.accordsSpeciaux?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Accords spéciaux</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('accordsSpeciaux') ? tagCategories.accordsSpeciaux : tagCategories.accordsSpeciaux.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.accordsSpeciaux.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('accordsSpeciaux') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'accordsSpeciaux']))}>
                    Charger plus ({tagCategories.accordsSpeciaux.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section Harmonie - Fonctions et cadences */}
          {(tagCategories.cadences?.length > 0 || tagCategories.retards?.length > 0) && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Cadences et retards</h4>
              
              {/* Cadences */}
              {tagCategories.cadences?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Cadences</label>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('cadences') ? tagCategories.cadences : tagCategories.cadences.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.cadences.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('cadences') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'cadences']))}>
                        Charger plus ({tagCategories.cadences.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Retards */}
              {tagCategories.retards?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Retards</label>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('retards') ? tagCategories.retards : tagCategories.retards.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.retards.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('retards') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'retards']))}>
                        Charger plus ({tagCategories.retards.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section Structure et degrés */}
          {(tagCategories.degres?.length > 0 || tagCategories.structure?.length > 0) && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Structure et degrés</h4>
              
              {/* Degrés */}
              {tagCategories.degres?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Degrés</label>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('degres') ? tagCategories.degres : tagCategories.degres.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.degres.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('degres') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'degres']))}>
                        Charger plus ({tagCategories.degres.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Structure */}
              {tagCategories.structure?.length > 0 && (
                <div className="free-mode-filter-subsection">
                  <label className="free-mode-filter-subsection-label">Structure</label>
                  <div className="free-mode-tags-container">
                    {tagCategories.structure.map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section Fonctions harmoniques */}
          {tagCategories.fonctionsHarmoniques?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Fonctions harmoniques</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('fonctionsHarmoniques') ? tagCategories.fonctionsHarmoniques : tagCategories.fonctionsHarmoniques.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.fonctionsHarmoniques.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('fonctionsHarmoniques') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'fonctionsHarmoniques']))}>
                    Charger plus ({tagCategories.fonctionsHarmoniques.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section Progressions */}
          {tagCategories.progressions?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Progressions harmoniques</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('progressions') ? tagCategories.progressions : tagCategories.progressions.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.progressions.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('progressions') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'progressions']))}>
                    Charger plus ({tagCategories.progressions.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section Contexte modal */}
          {tagCategories.contexteModal?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Contexte modal</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('contexteModal') ? tagCategories.contexteModal : tagCategories.contexteModal.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.contexteModal.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('contexteModal') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'contexteModal']))}>
                    Charger plus ({tagCategories.contexteModal.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section Styles */}
          {tagCategories.styles?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Période stylistique</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('styles') ? tagCategories.styles : tagCategories.styles.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.styles.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('styles') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'styles']))}>
                    Charger plus ({tagCategories.styles.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section Formations (tags instrumentation) */}
          {tagCategories.formations?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Formation (instrumentation)</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('formations') ? tagCategories.formations : tagCategories.formations.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.formations.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('formations') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'formations']))}>
                    Charger plus ({tagCategories.formations.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section Genres (tags type d'œuvre) */}
          {tagCategories.genres?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Genre (type d&apos;œuvre)</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('genres') ? tagCategories.genres : tagCategories.genres.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.genres.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('genres') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'genres']))}>
                    Charger plus ({tagCategories.genres.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}
            </div>
          </details>
          </div>

          <div className="free-mode-filters-footer">
            <span className="free-mode-filters-footer-count">
              {activeFilterCount} filtre{activeFilterCount > 1 ? 's' : ''} actif{activeFilterCount > 1 ? 's' : ''}
            </span>
            {hasActiveFilters && (
              <button type="button" className="free-mode-clear-filters" onClick={clearAllFilters}>
                Tout effacer
              </button>
            )}
          </div>
        </aside>
        
        {/* Zone de résultats */}
        <div className="free-mode-results">
          {activeFilterChips.length > 0 && (
            <div className="free-mode-applied-filters">
              <div className="free-mode-applied-filters-header">
                <p>Filtres appliqués ({activeFilterChips.length})</p>
                <button type="button" className="free-mode-applied-filters-clear" onClick={clearAllFilters}>
                  Tout effacer
                </button>
              </div>
              <div className="free-mode-applied-filters-list">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className="free-mode-applied-filter-chip"
                    onClick={() => removeFilterChip(chip)}
                    aria-label={`Retirer ${chip.label}`}
                  >
                    <span>{chip.label}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading ? (
            <div className="free-mode-loading">
              <div className="spinner"></div>
              <p>Chargement des exercices...</p>
            </div>
          ) : filteredExercises.length === 0 ? (
            <div className="free-mode-empty">
              <div className="free-mode-empty-icon">🔍</div>
              <h3 className="free-mode-empty-title">Aucun exercice trouvé</h3>
              <p className="free-mode-empty-text">
                Essaye de modifier tes critères de recherche ou tes filtres pour trouver plus d'exercices.
              </p>
              {hasActiveFilters && (
                <div className="free-mode-empty-actions">
                  <button className="free-mode-empty-cta" onClick={removeLastAppliedFilter}>
                    Retirer le dernier filtre
                  </button>
                  <button className="free-mode-empty-cta free-mode-empty-cta-secondary" onClick={clearAdvancedFilters}>
                    Effacer les filtres avancés
                  </button>
                  <button className="free-mode-empty-cta free-mode-empty-cta-secondary" onClick={clearAllFilters}>
                    Réinitialiser tout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="free-mode-results-header">
                <p className="free-mode-results-count">
                  {sortedExercises.length} exercice{sortedExercises.length !== 1 ? 's' : ''} trouvé{sortedExercises.length !== 1 ? 's' : ''}
                </p>
                <div className="free-mode-sort">
                  <label htmlFor="free-mode-sort-select" className="free-mode-sort-label">Trier par</label>
                  <select
                    id="free-mode-sort-select"
                    className="free-mode-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Trier par"
                  >
                    <option value="date">Derniers ajoutés</option>
                    <option value="title">Titre (A→Z)</option>
                    <option value="composer">Compositeur (A→Z)</option>
                  </select>
                </div>
              </div>
              <div className="free-mode-grid">
                {sortedExercises.map((exercise) => {
                  const vr = videoRanksMap.get(exercise.id)
                  return (
                  <ExerciseCard
                    key={exercise.id}
                    exercise={exercise}
                    allExercises={sortedExercises}
                    videoRank={vr?.rank}
                    videoGroupSize={vr?.groupSize}
                    showDemoTeaserBadge={publicTeaserOnly}
                    onClick={(id) => handleExerciseClick(id)}
                    onPillClick={(payload) => {
                      if (payload.type === 'difficulty') setSelectedDifficulty((prev) => (prev === payload.value ? '' : payload.value))
                      else if (payload.type === 'tag') setSelectedTags((prev) => (prev.includes(payload.value) ? prev.filter((t) => t !== payload.value) : [...prev, payload.value]))
                      else if (payload.type === 'genre') setSelectedGenre((prev) => (prev === payload.value ? '' : payload.value))
                      else if (payload.type === 'formation') setSelectedFormations((prev) => (prev.includes(payload.value) ? prev.filter((id) => id !== payload.value) : [...prev, payload.value]))
                    }}
                    onOpenCodex={onOpenCodex}
                    recommendedLabel={doneSet.has(exercise.id) ? 'Déjà fait' : undefined}
                  />
                )})}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Drawer de filtres (mobile) */}
      {isFilterDrawerOpen && (
        <>
          <div
            className="free-mode-filter-drawer-overlay"
            {...filterDrawerBackdropDismiss}
          ></div>
          <div className="free-mode-filter-drawer">
            <div className="free-mode-filter-drawer-header">
              <h3>Filtres</h3>
              <button
                className="free-mode-filter-drawer-close"
                onClick={() => setIsFilterDrawerOpen(false)}
                aria-label="Fermer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="free-mode-filter-drawer-content">
              {/* Statut */}
              <div className="free-mode-filter-block free-mode-filter-block-statut">
                <h4 className="free-mode-filter-block-title">Statut</h4>
                <div className="free-mode-filter-group">
                  <div className="free-mode-statut-chips">
                    <button
                      type="button"
                      className={`free-mode-statut-chip ${selectedDoneStatus === 'all' ? 'free-mode-statut-chip-active' : ''}`}
                      onClick={() => setSelectedDoneStatus('all')}
                    >
                      Tous
                    </button>
                    <button
                      type="button"
                      className={`free-mode-statut-chip ${selectedDoneStatus === 'done' ? 'free-mode-statut-chip-active' : ''}`}
                      onClick={() => setSelectedDoneStatus('done')}
                    >
                      Déjà fait
                    </button>
                    <button
                      type="button"
                      className={`free-mode-statut-chip ${selectedDoneStatus === 'not-done' ? 'free-mode-statut-chip-active' : ''}`}
                      onClick={() => setSelectedDoneStatus('not-done')}
                    >
                      Pas encore fait
                    </button>
                  </div>
                </div>
              </div>

              {/* Accès rapide */}
              <div className="free-mode-filter-block free-mode-filter-block-quick">
                <h4 className="free-mode-filter-block-title">Accès rapide</h4>
                <div className="free-mode-filter-group">
                  <label className="free-mode-filter-label">Niveau</label>
                  <div className="free-mode-difficulty-cards">
                    {difficulties.map(diff => (
                      <button
                        key={diff}
                        className={`free-mode-difficulty-card ${selectedDifficulty === diff ? 'free-mode-difficulty-card-active' : ''}`}
                        onClick={() => setSelectedDifficulty(selectedDifficulty === diff ? '' : diff)}
                      >
                        <span className="free-mode-difficulty-icon">
                          {diff === 'facile' && '🌱'}
                          {diff === 'intermédiaire' && '⭐'}
                          {diff === 'difficile' && '🔥'}
                          {diff === 'expert' && '💎'}
                        </span>
                        <span className="free-mode-difficulty-label">{diff}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {notionQuickFilters.length > 0 && (
                  <div className="free-mode-filter-group">
                    <label className="free-mode-filter-label">Filtrer par notion</label>
                    <div className="free-mode-notion-chips">
                      {notionQuickFilters.map(({ label, tag }) => (
                        <button
                          key={tag}
                          type="button"
                          className={`free-mode-notion-chip ${selectedTags.includes(tag) ? 'free-mode-notion-chip-active' : ''}`}
                          onClick={() => applyNotionQuickFilter(tag)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Affiner */}
              <div className="free-mode-filter-block">
                <h4 className="free-mode-filter-block-title">Affiner</h4>
                <div className="free-mode-filter-group">
                  <label className="free-mode-filter-label">Compositeur</label>
                  <div className="free-mode-chips-container">
                    <button
                      className={`free-mode-chip ${selectedComposer === '' ? 'free-mode-chip-active' : ''}`}
                      onClick={() => setSelectedComposer('')}
                    >
                      Tous
                    </button>
                    {(composersExpanded ? composers : composers.slice(0, COMPOSERS_VISIBLE_INITIAL)).map(composer => (
                      <button
                        key={composer}
                        className={`free-mode-chip ${selectedComposer === composer ? 'free-mode-chip-active' : ''}`}
                        onClick={() => setSelectedComposer(composer)}
                      >
                        {composer}
                      </button>
                    ))}
                    {composers.length > COMPOSERS_VISIBLE_INITIAL && !composersExpanded && (
                      <button
                        type="button"
                        className="free-mode-chip free-mode-chip-load-more"
                        onClick={() => setComposersExpanded(true)}
                      >
                        Charger plus ({composers.length - COMPOSERS_VISIBLE_INITIAL})
                      </button>
                    )}
                  </div>
                </div>
                {genreOptions.length > 0 && (
                  <div className="free-mode-filter-group">
                    <label className="free-mode-filter-label">Genre (type d&apos;œuvre)</label>
                    <div className="free-mode-chips-container">
                      <button
                        className={`free-mode-chip ${selectedGenre === '' ? 'free-mode-chip-active' : ''}`}
                        onClick={() => setSelectedGenre('')}
                      >
                        Tous
                      </button>
                      {genreOptions.map(g => (
                        <button
                          key={g.id}
                          className={`free-mode-chip ${selectedGenre === g.id ? 'free-mode-chip-active' : ''}`}
                          onClick={() => setSelectedGenre(selectedGenre === g.id ? '' : g.id)}
                        >
                          {g.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="free-mode-filter-group">
                  <label className="free-mode-filter-label">Formation (instrumentation)</label>
                  <div className="free-mode-chips-container">
                    <button
                      type="button"
                      className={`free-mode-chip ${selectedFormations.length === 0 ? 'free-mode-chip-active' : ''}`}
                      onClick={() => setSelectedFormations([])}
                    >
                      Toutes
                    </button>
                  </div>
                  <FormationInstrumentPicker
                    masterList={CLASSICAL_FORMATIONS}
                    value={selectedFormations}
                    onChange={setSelectedFormations}
                    prioritizedIds={formationCorpusIds}
                    theme="freeMode"
                    showClearAll={false}
                    hint="Les instruments déjà présents dans le catalogue apparaissent en premier — tape pour rechercher dans la liste complète."
                  />
                </div>
              </div>

              {/* Détails techniques */}
              <div className="free-mode-filter-block free-mode-filter-block-details">
                <h4 className="free-mode-filter-block-title">Détails techniques</h4>
              
              {/* Section Harmonie - Structure des accords */}
              {(tagCategories.renversements?.length > 0 || tagCategories.extensions?.length > 0 || tagCategories.qualites?.length > 0) && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Structure des accords</h4>
                  
                  {/* Renversements */}
                  {tagCategories.renversements?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Renversements</label>
                      <div className="free-mode-tags-container">
                        {(expandedTagSections.has('renversements') ? tagCategories.renversements : tagCategories.renversements.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                        {tagCategories.renversements.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('renversements') && (
                          <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'renversements']))}>
                            Charger plus ({tagCategories.renversements.length - TAGS_PER_SECTION_VISIBLE})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Extensions */}
                  {tagCategories.extensions?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Extensions</label>
                      <div className="free-mode-tags-container">
                        {(expandedTagSections.has('extensions') ? tagCategories.extensions : tagCategories.extensions.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                        {tagCategories.extensions.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('extensions') && (
                          <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'extensions']))}>
                            Charger plus ({tagCategories.extensions.length - TAGS_PER_SECTION_VISIBLE})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Qualités */}
                  {tagCategories.qualites?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Qualités</label>
                      <div className="free-mode-tags-container">
                        {(expandedTagSections.has('qualites') ? tagCategories.qualites : tagCategories.qualites.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                        {tagCategories.qualites.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('qualites') && (
                          <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'qualites']))}>
                            Charger plus ({tagCategories.qualites.length - TAGS_PER_SECTION_VISIBLE})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tagCategories.accordsSpeciaux?.length > 0 && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Accords spéciaux</h4>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('accordsSpeciaux') ? tagCategories.accordsSpeciaux : tagCategories.accordsSpeciaux.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.accordsSpeciaux.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('accordsSpeciaux') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'accordsSpeciaux']))}>
                        Charger plus ({tagCategories.accordsSpeciaux.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Section Harmonie - Fonctions et cadences */}
              {(tagCategories.cadences?.length > 0 || tagCategories.retards?.length > 0) && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Cadences et retards</h4>
                  
                  {/* Cadences */}
                  {tagCategories.cadences?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Cadences</label>
                      <div className="free-mode-tags-container">
                        {(expandedTagSections.has('cadences') ? tagCategories.cadences : tagCategories.cadences.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                        {tagCategories.cadences.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('cadences') && (
                          <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'cadences']))}>
                            Charger plus ({tagCategories.cadences.length - TAGS_PER_SECTION_VISIBLE})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Retards */}
                  {tagCategories.retards?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Retards</label>
                      <div className="free-mode-tags-container">
                        {(expandedTagSections.has('retards') ? tagCategories.retards : tagCategories.retards.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                        {tagCategories.retards.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('retards') && (
                          <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'retards']))}>
                            Charger plus ({tagCategories.retards.length - TAGS_PER_SECTION_VISIBLE})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Section Structure et degrés */}
              {(tagCategories.degres?.length > 0 || tagCategories.structure?.length > 0) && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Structure et degrés</h4>
                  
                  {/* Degrés */}
                  {tagCategories.degres?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Degrés</label>
                      <div className="free-mode-tags-container">
                        {(expandedTagSections.has('degres') ? tagCategories.degres : tagCategories.degres.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                        {tagCategories.degres.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('degres') && (
                          <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'degres']))}>
                            Charger plus ({tagCategories.degres.length - TAGS_PER_SECTION_VISIBLE})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Structure */}
                  {tagCategories.structure?.length > 0 && (
                    <div className="free-mode-filter-subsection">
                      <label className="free-mode-filter-subsection-label">Structure</label>
                      <div className="free-mode-tags-container">
                        {tagCategories.structure.map(tag => {
                          const formatted = formatTagForDisplay(tag);
                          return (
                            <button
                              key={tag}
                              className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                              onClick={() => toggleTag(tag)}
                            >
                              {formatted}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Section Fonctions harmoniques */}
              {tagCategories.fonctionsHarmoniques?.length > 0 && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Fonctions harmoniques</h4>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('fonctionsHarmoniques') ? tagCategories.fonctionsHarmoniques : tagCategories.fonctionsHarmoniques.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.fonctionsHarmoniques.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('fonctionsHarmoniques') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'fonctionsHarmoniques']))}>
                        Charger plus ({tagCategories.fonctionsHarmoniques.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Section Progressions */}
              {tagCategories.progressions?.length > 0 && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Progressions harmoniques</h4>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('progressions') ? tagCategories.progressions : tagCategories.progressions.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.progressions.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('progressions') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'progressions']))}>
                        Charger plus ({tagCategories.progressions.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Section Contexte modal */}
              {tagCategories.contexteModal?.length > 0 && (
                <div className="free-mode-filter-section">
                  <h4 className="free-mode-filter-section-title">Contexte modal</h4>
                  <div className="free-mode-tags-container">
                    {(expandedTagSections.has('contexteModal') ? tagCategories.contexteModal : tagCategories.contexteModal.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                      const formatted = formatTagForDisplay(tag);
                      return (
                        <button
                          key={tag}
                          className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                          onClick={() => toggleTag(tag)}
                        >
                          {formatted}
                        </button>
                      );
                    })}
                    {tagCategories.contexteModal.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('contexteModal') && (
                      <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'contexteModal']))}>
                        Charger plus ({tagCategories.contexteModal.length - TAGS_PER_SECTION_VISIBLE})
                      </button>
                    )}
                  </div>
                </div>
              )}

          {/* Section Styles */}
          {tagCategories.styles?.length > 0 && (
            <div className="free-mode-filter-section">
              <h4 className="free-mode-filter-section-title">Période stylistique</h4>
              <div className="free-mode-tags-container">
                {(expandedTagSections.has('styles') ? tagCategories.styles : tagCategories.styles.slice(0, TAGS_PER_SECTION_VISIBLE)).map(tag => {
                  const formatted = formatTagForDisplay(tag);
                  return (
                    <button
                      key={tag}
                      className={`free-mode-tag-chip ${selectedTags.includes(tag) ? 'free-mode-tag-chip-active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {formatted}
                    </button>
                  );
                })}
                {tagCategories.styles.length > TAGS_PER_SECTION_VISIBLE && !expandedTagSections.has('styles') && (
                  <button type="button" className="free-mode-tag-chip free-mode-tag-chip-load-more" onClick={() => setExpandedTagSections(s => new Set([...s, 'styles']))}>
                    Charger plus ({tagCategories.styles.length - TAGS_PER_SECTION_VISIBLE})
                  </button>
                )}
              </div>
            </div>
          )}
              </div>
            </div>
            
            <div className="free-mode-filter-drawer-footer">
              <button
                className="free-mode-filter-drawer-apply"
                onClick={() => setIsFilterDrawerOpen(false)}
              >
                Appliquer les filtres
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default FreeMode
